import { call } from '@apparel/core/utils';
import { cx } from '@apparel/react/cx';
import CircularProgress from '@material-ui/core/CircularProgress';
import Fade from '@material-ui/core/Fade';
import Grow from '@material-ui/core/Grow';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Tooltip from '@material-ui/core/Tooltip';
import PanoramaIcon from '@material-ui/icons/Panorama';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImageObject } from './ImageObject';
import { ImageProps } from './ImageProps';

const useStyles = makeStyles(
    () => ({
        root: ({ radius, aspectRatio = 16 / 9 }: any) => ({
            display: 'block',
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            paddingBottom: `${100 / aspectRatio}%`,
            borderRadius: radius,
            fontSize: 0,
        }),

        image: ({ objectFit = 'cover', objectPosition = 'center center' }: Record<string, any>) => ({
            objectFit,
            objectPosition,
        }),

        progressHolder: {
            zIndex: 1,
            position: 'absolute',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            top: '50%',
            left: '50%',
            transform: 'translate3d(-50%, -50%, 0)',
            fontSize: 0,
        },

        progress: {},

        content: {
            zIndex: 5,
        },

        imageError: {
            display: 'none !important',
        },

        imageErrorView: {
            top: 0,
            left: 0,
            zIndex: 7,
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',

            '& .icon': {
                fontSize: 48,
            },
        },

        inner: {
            zIndex: 3,

            '&, & $image, & $content': {
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
            },
        },
    }),
    {
        name: 'Image',
    },
);

const observer =
    typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
              (entries, observer) => {
                  entries.forEach((entry) => {
                      if (entry.isIntersecting) {
                          entry.target.dispatchEvent(new Event('intersect'));
                          observer.unobserve(entry.target);
                      }
                  });
              },
              { threshold: 0.1 },
          )
        : null;

/**
 *
 * @author: Dumitrana Alinus <alinus@wooter.co>
 * @component: Image
 * @date: 26.12.2020
 * @time: 18:58
 */
export function Image<C extends React.ElementType = 'div'>({
    source,
    width,
    height,
    alt,
    aspectRatio,
    className,
    burstCache,
    objectFit,
    objectPosition,
    children,
    radius,
    component,
    imgProps,
    classes = {},
    variant = 'thumbnail',

    ...rest
}: ImageProps<C>) {
    const defaultClasses = useStyles({ aspectRatio, objectFit, objectPosition, radius });
    const rootRef = useRef<HTMLElement>();
    const Component = useMemo(() => component ?? 'div', [component]);
    const imgObject = useMemo(() => ImageObject.from(source), [source]);
    const source$ = useMemo(() => imgObject?.getSource(variant), [imgObject, variant]);

    const [src, setSrc] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    const handleIntersect = useCallback(() => {
        setSrc(source$);
        setIsLoading(true);
        setIsError(false);
    }, [setSrc, setIsLoading, setIsError, source$]);

    const handleLoad = useCallback(
        (e) => {
            setIsLoading(false);
            call(imgProps?.onLoad, e);
        },
        [setIsError, imgProps?.onLoad],
    );

    const handleError = useCallback(
        (e) => {
            setIsError(true);
            setIsLoading(false);
            call(imgProps?.onError, e);
        },
        [setIsError, setIsLoading, imgProps?.onError],
    );

    const [img$, setImg] = useState(() => <img src={source$} alt={alt} loading={'lazy'} style={{ display: 'none' }} />);

    useEffect(() => {
        setImg(
            <Grow in={!isLoading} exit={false}>
                <img
                    {...imgProps}
                    className={cx(defaultClasses.image, classes.image, imgProps?.className, {
                        [cx(defaultClasses.imageError, classes.imageError)]: !src || isError,
                    })}
                    src={src}
                    alt={alt}
                    onLoad={handleLoad}
                    onError={handleError}
                    loading={'lazy'}
                />
            </Grow>,
        );
    }, [
        src,
        alt,
        handleLoad,
        handleError,
        isError,
        isLoading,
        defaultClasses.image,
        classes.image,
        defaultClasses.imageError,
        classes.imageError,
        imgProps,
    ]);

    useEffect(() => {
        setSrc('');
        setIsError(false);
    }, [source$]);

    useEffect(() => {
        rootRef.current?.addEventListener('intersect', handleIntersect);
        return () => {
            rootRef.current?.removeEventListener('intersect', handleIntersect);
        };
    }, [handleIntersect]);

    useEffect(() => {
        const el = rootRef.current;
        observer?.observe(el);

        return () => {
            observer?.unobserve(el);
        };
    }, [src, rootRef.current]);

    return (
        <Component ref={rootRef as any} className={cx(defaultClasses.root, classes.root, className)} {...rest}>
            <div className={cx(defaultClasses.inner, classes.inner)}>
                {img$}

                <Grow in={!src || isError} unmountOnExit>
                    <div className={defaultClasses.imageErrorView}>
                        <Tooltip title={"Image couldn't be found or load."}>
                            <PanoramaIcon className={'icon'} color={'disabled'} />
                        </Tooltip>
                    </div>
                </Grow>

                <Fade in={source$ && isLoading} unmountOnExit>
                    <span className={cx(defaultClasses.progressHolder, classes.progressHolder)}>
                        <CircularProgress
                            variant={'indeterminate'}
                            size={34}
                            thickness={2}
                            color={'primary'}
                            className={cx(defaultClasses.progress, classes.progress)}
                        />
                    </span>
                </Fade>

                {!!children && <div className={cx(defaultClasses.content, classes.content)}>{children}</div>}
            </div>
        </Component>
    );
}

export default Image;
